import "@assets/css/mode_tool/panel.css";
import AppModal from "@components/modals/AppModal";
import ModeToolHeader from "@components/mode_tool/header";
import ModeToolModeList from "@components/mode_tool/modeList";
import ModeToolPointsList from "@components/mode_tool/pointsList";
import RouteActionsOverlayPanel from "@components/maps/RouteActionsOverlayPanel";
import TranslatePointsModal from "@components/mode_tool/TranslatePointsModal";
import AutomateRoutePointsModal from "@components/mode_tool/AutomateRoutePointsModal";
import AiAudioLanguageModal from "@components/mode_tool/AiAudioLanguageModal";
import AiMusicModal from "@components/mode_tool/AiMusicModal";
import { AiSpeakerConfiguration } from "@components/mode_tool/AiSpeakerConfiguration";
import useStartPointLanguages from "@hooks/start_point/useStartPointLanguages";
import useRoutePointTranslation from "@hooks/mode_tool/useRoutePointTranslation";
import useRoutePointAutomation from "@hooks/mode_tool/useRoutePointAutomation";
import useRouteMusicGeneration from "@hooks/mode_tool/useRouteMusicGeneration";
import useRouteAudioMixing from "@hooks/mode_tool/useRouteAudioMixing";
import useBatchAudioDownload from "@hooks/mode_tool/useBatchAudioDownload";
import useBatchAudioUpload from "@hooks/mode_tool/useBatchAudioUpload";
import { isDirectionPoint } from "@utils/mode_tool/routePointAutomation";
import {
  generateTextToSpeechStream,
  listAiVoicesCatalogRequest,
  searchAiVoicesRequest,
} from "@services/ai/aiApi";
import { resolveMediaKind, resolveMediaUrl } from "@utils/media/mediaUrl";
import { useEffect, useMemo, useRef, useState } from "react";

function readLocalizedValue(entry) {
  if (typeof entry === "string") return entry;
  if (entry && typeof entry === "object") return entry.value || "";
  return "";
}

function resolveSelectedStartPointName(point = {}, activeLanguageIso = "") {
  const localizedName =
    readLocalizedValue(point?.name?.[activeLanguageIso]) ||
    readLocalizedValue(point?.name?.en) ||
    readLocalizedValue(point?.name?.es) ||
    "";
  return (
    String(localizedName || "").trim() ||
    String(
      point?.zoneName ||
        point?.zone ||
        point?.placeName ||
        point?.locationName ||
        point?.id ||
        "",
    ).trim()
  );
}

function readPlainText(value) {
  return String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
}

function BulkAiAudioDescription({
  isOpen,
  onClose,
  targetLanguageIso,
  targetPointScope,
  selectedMode,
  modePoints = [],
  selectedWalkingRouteKey = "",
  walkingRouteMetaByKey = {},
  walkingPointsByRoute = {},
  onCommitPoint,
  onCommitBulkPoint,
}) {
  const [catalog, setCatalog] = useState({
    language: [], gender: [], pitch: [], style: [], useCase: [],
  });
  const [filters, setFilters] = useState({
    language: "", gender: "", pitch: "", style: "", useCase: "", search: "",
  });
  const [groups, setGroups] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setProgress(null);
    setError("");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let isCurrent = true;
    setIsLoading(true);
    setError("");
    listAiVoicesCatalogRequest()
      .then((catalogResponse) => {
        if (!isCurrent) return;
        setCatalog(catalogResponse?.body || {});
      })
      .catch((requestError) => {
        if (isCurrent) setError(requestError?.message || "Could not load AI voices.");
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });
    return () => { isCurrent = false; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let isCurrent = true;
    const timeoutId = window.setTimeout(() => {
      setIsLoading(true);
      searchAiVoicesRequest(filters)
        .then((response) => {
          if (isCurrent) setGroups(Array.isArray(response?.body?.groups) ? response.body.groups : []);
        })
        .catch((requestError) => {
          if (isCurrent) setError(requestError?.message || "Could not search AI voices.");
        })
        .finally(() => {
          if (isCurrent) setIsLoading(false);
        });
    }, 350);
    return () => {
      isCurrent = false;
      window.clearTimeout(timeoutId);
    };
  }, [
    isOpen,
    filters.language,
    filters.gender,
    filters.pitch,
    filters.style,
    filters.useCase,
    filters.search,
  ]);

  function hasAiAudio(point) {
    const saved =
      point?.aiAudioDescription?.[targetLanguageIso] ||
      point?.ai_audio_description?.[targetLanguageIso] ||
      {};
    return Boolean(saved?.audio?.id || saved?.audio?.url);
  }

  async function generateAll() {
    if (!targetLanguageIso) {
      setError("Select an audio language before generating audio.");
      return;
    }
    if (!selectedVoice?.id) {
      setError("Select a voice before generating audio.");
      return;
    }
    const allPoints = selectedMode === "walking"
      ? (Array.isArray(walkingPointsByRoute?.[selectedWalkingRouteKey])
          ? walkingPointsByRoute[selectedWalkingRouteKey]
          : []).map((point) => ({
            point,
            routeKey: selectedWalkingRouteKey,
            routeName:
              readLocalizedValue(
                walkingRouteMetaByKey?.[selectedWalkingRouteKey]?.name?.[
                  targetLanguageIso
                ],
              ) || "Walking route",
          }))
      : (Array.isArray(modePoints) ? modePoints : []).map((point) => ({
          point,
          routeKey: "",
          routeName: "",
        }));
    const scopedPoints = allPoints.filter(({ point }) => targetPointScope === "all"
      || (targetPointScope === "directions"
        ? isDirectionPoint(point)
        : !isDirectionPoint(point)));
    const candidates = scopedPoints.filter(
      ({ point }) => readPlainText(readLocalizedValue(point?.description?.[targetLanguageIso])) && !hasAiAudio(point),
    );
    if (!candidates.length) {
      setError("No eligible points were found for this selection and language.");
      return;
    }

    const handledIds = new Set();
    const candidatesById = new Map(candidates.map((candidate) => [String(candidate.point?.id || ""), candidate]));
    const progressPoints = candidates.map(({ point, routeName }, index) => ({
      index,
      id: String(point?.id || ""),
      label: `${routeName ? `${routeName} · ` : ""}${readLocalizedValue(point?.name?.[targetLanguageIso]) || readLocalizedValue(point?.name?.en) || `Point ${point?.order || index + 1}`}`,
      status: "pending",
    }));
    setIsGenerating(true);
    setError("");
    setProgress({ current: 0, total: candidates.length, points: progressPoints, done: false, completeReceived: false });

    const applyAudioResult = (eventData) => {
      const row = eventData?.point ?? eventData?.data ?? eventData;
      const id = String(eventData?.id || row?.id || "");
      const candidate = candidatesById.get(id);
      if (!candidate || handledIds.has(id)) return;
      const { point, routeKey } = candidate;
      const rowStatus = String(eventData?.status || (row?.error ? "error" : "completed"));
      if (rowStatus === "error" || row?.error === true) {
        handledIds.add(id);
        return;
      }
      const multimedia = row?.multimedia;
      if (!multimedia?.id || !multimedia?.url) {
        handledIds.add(id);
        return;
      }
      const text = readPlainText(readLocalizedValue(point?.description?.[targetLanguageIso]));
      const audio = {
        ...multimedia,
        url: resolveMediaUrl(String(multimedia.url || "")),
        mediaType: "audio",
        language: targetLanguageIso,
      };
      const existingAi = point?.aiAudioDescription || point?.ai_audio_description || {};
      const remainingMedia = (Array.isArray(point?.multimedia) ? point.multimedia : []).filter(
        (item) => !(resolveMediaKind(item) === "audio" && String(item?.language || item?.languageIso || "").toLowerCase() === targetLanguageIso),
      );
      const nextPoint = {
        ...point,
        multimedia: [...remainingMedia, audio],
        aiAudioDescription: {
          ...existingAi,
          [targetLanguageIso]: {
            ...(existingAi?.[targetLanguageIso] || {}),
            description: text,
            speakers: [{
              speaker: selectedVoice.name,
              voice: selectedVoice.id,
              language: filters.language,
              gender: filters.gender || selectedVoice.gender || "",
              pitch: filters.pitch || selectedVoice.pitch || "",
              style: filters.style,
              use_case: filters.useCase,
            }],
            voiceSegments: [],
            audio,
          },
        },
      };
      if (selectedMode === "walking") {
        onCommitBulkPoint?.(routeKey, point.id, nextPoint);
      } else {
        onCommitPoint?.(point.id, nextPoint);
      }
      handledIds.add(id);
    };

    const updateProgressPoint = (eventData) => {
      const row = eventData?.point ?? eventData?.data ?? eventData;
      const id = String(eventData?.id || row?.id || "");
      const status = String(eventData?.status || (row?.error ? "error" : "completed"));
      setProgress((current) => {
        if (!current) return current;
        const nextPoints = current.points.map((item) =>
          item.id === id
            ? { ...item, status, message: String(row?.message || eventData?.message || "") }
            : item,
        );
        return {
          ...current,
          points: nextPoints,
          current: nextPoints.filter((item) => item.status !== "pending").length,
        };
      });
    };

    try {
      const payload = candidates.map(({ point }) => ({
        id: String(point.id),
        text: readPlainText(readLocalizedValue(point?.description?.[targetLanguageIso])),
        voice: String(selectedVoice.id),
      }));
      const response = await generateTextToSpeechStream(payload, ({ event, data }) => {
        if (event === "start") {
          setProgress((current) => current ? { ...current, total: Number(data?.total) || current.total } : current);
        } else if (event === "point") {
          applyAudioResult(data);
          updateProgressPoint(data);
        } else if (event === "complete") {
          setProgress((current) => current ? { ...current, done: true, completeReceived: true } : current);
        }
      });
      if (response?.error || response?.success === false) {
        throw new Error(response?.message || "Audio generation failed.");
      }
      const responseData = response?.body?.data;
      if (Array.isArray(responseData)) {
        responseData.forEach((row) => {
          applyAudioResult({ id: row?.id, point: row, status: row?.error ? "error" : "completed" });
          updateProgressPoint({ id: row?.id, point: row, status: row?.error ? "error" : "completed" });
        });
      }
      setProgress((current) => current ? { ...current, done: true, completeReceived: true } : current);
    } catch (generationError) {
      const message = generationError?.message || "Audio generation failed.";
      setError(message);
      setProgress((current) => current ? {
        ...current,
        points: current.points.map((item) => item.status === "pending"
          ? { ...item, status: "error", message }
          : item),
        current: current.points.length,
        done: true,
      } : current);
    } finally {
      setProgress((current) => current ? { ...current, done: true } : current);
      setIsGenerating(false);
    }
  }

  const flatVoices = groups.flatMap((group) =>
    (Array.isArray(group?.voices) ? group.voices : []).map((voice) => ({ ...voice, group: group.name })),
  );
  const updateFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  return (
    <AppModal isOpen={isOpen} onClose={() => !isGenerating && onClose?.()} closeDisabled={isGenerating} title={`AI Audio Description (${String(targetLanguageIso || "").toUpperCase()})`} size="lg" className="mode-route-description-modal is-ai-audio-description mode-route-bulk-ai-modal">
        <div className="mode-route-description-ai-form mode-route-bulk-ai-form">
          <p>Choose one voice. Only points without AI Audio Description in {String(targetLanguageIso || "").toUpperCase()} will be generated.</p>
          <AiSpeakerConfiguration
            number={1}
            settings={filters}
            catalog={catalog}
            voices={flatVoices}
            voiceGroups={groups}
            selectedVoice={selectedVoice?.id || ""}
            selectedVoiceDetails={selectedVoice}
            isLoadingCatalog={isLoading}
            isLoadingVoices={isLoading}
            onSettingChange={updateFilter}
            onVoiceChange={(voiceId) =>
              setSelectedVoice(
                flatVoices.find((voice) => voice.id === voiceId) || {
                  id: voiceId,
                  name: voiceId,
                },
              )
            }
            searchTerm={filters.search}
            onSearchChange={(value) => updateFilter("search", value)}
          />
          {progress ? (
            <section className={`mode-route-bulk-ai-stream mode-route-ai-stream ${isGenerating ? "is-running" : "is-finished"}`} aria-live="polite">
              <div className="mode-route-bulk-ai-stream-heading">
                <strong>{progress.done ? "Audio generation summary" : "Generating route audios"}</strong>
                <span>{progress.current} / {progress.total}</span>
              </div>
              <div className="mode-route-ai-stream-track" role="progressbar" aria-label="Audio generation progress" aria-valuemin={0} aria-valuemax={progress.total} aria-valuenow={progress.current}>
                <span style={{ width: `${progress.total ? Math.min(100, Math.round((progress.current / progress.total) * 100)) : 0}%` }} />
              </div>
              <div className="mode-route-bulk-ai-stream-list">
                {progress.points.map((item, index) => {
                  const status = String(item.status || "pending");
                  const statusLabel = { completed: "Saved", error: "Error", pending: "Waiting" }[status] || status;
                  const statusIcon = { completed: "✓", error: "!", pending: "…" }[status] || "•";
                  return (
                    <div className={`mode-route-bulk-ai-stream-item is-${status}`} key={item.id || index}>
                      <span className="mode-route-ai-stream-status" aria-hidden="true">{statusIcon}</span>
                      <span className="mode-route-ai-stream-point">
                        <strong>#{index + 1} · {item.label}</strong>
                        {item.message ? <small>{item.message}</small> : null}
                      </span>
                      <span className="mode-route-ai-stream-label">{statusLabel}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          ) : null}
          {error ? <p className="message-error">{error}</p> : null}
          <div className="tour-add-editor-actions is-ai-audio-description-actions mode-route-bulk-ai-actions">
            <button type="button" className="button-ghost" onClick={onClose} disabled={isGenerating}>
              Close
            </button>
            <div className="tour-add-editor-actions-right">
              {!progress?.completeReceived ? (
                <button type="button" className="button-primary" onClick={generateAll} disabled={isGenerating || !selectedVoice}>
                  {isGenerating ? "Generating..." : "Generate"}
                </button>
              ) : null}
            </div>
          </div>
        </div>
    </AppModal>
  );
}

function ModeRouteToolPanel({
  startPointMode,
  tourId,
  selectedStartPoint,
  modeOptions,
  selectedMode,
  isPickingModePoints,
  modePointCount,
  modePoints,
  directiosApiRoutePointRaw,
  selectedPointId,
  onSelectMode,
  onTogglePickingModePoints,
  onStopWalkingRouteShapeEdit,
  onSelectPoint,
  languages,
  onCommitPoint,
  onUploadPointMedia,
  onDeletePoint,
  onSaveModeRedux,
  canSaveModePoints,
  modeValidationHint,
  saveModePointsHint,
  onCloseTool,
  walkingRouteKeys,
  selectedWalkingRouteKey,
  walkingRouteMetaByKey,
  walkingPointsByRoute,
  onCommitBulkAiAudioPoint,
  isWalkingRouteShapeEditEnabled,
  onSelectWalkingRoute,
  onAddWalkingRoute,
  onChangeWalkingRouteName,
  onToggleWalkingRouteShapeEdit,
  onDeleteWalkingRoute,
  onActiveLanguageChange,
  onImportJson,
  isImportingJson,
  onExportJson,
  canExportJson = false,
  importJsonMessage = "",
  importJsonError = "",
  onGenerateTurnByTurn,
  isGeneratingTurnByTurn = false,
  turnByTurnApplied = false,
  onApplyAutomatedPoints,
  tourName = "",
}) {
  const modeToolOverlayRef = useRef(null);
  const languageList = useStartPointLanguages(languages);
  const [activeLanguageIso, setActiveLanguageIso] = useState("");
  const [isBulkAiAudioOpen, setIsBulkAiAudioOpen] = useState(false);
  const [isAiAudioLanguageOpen, setIsAiAudioLanguageOpen] = useState(false);
  const [aiAudioLanguageIso, setAiAudioLanguageIso] = useState("");
  const [aiAudioPointScope, setAiAudioPointScope] = useState("all");
  function openAiAudioLanguagePicker() {
    setIsBulkAiAudioOpen(false);
    setAiAudioLanguageIso("");
    setAiAudioPointScope("all");
    setIsAiAudioLanguageOpen(true);
  }

  function selectAiAudioLanguage(languageIso) {
    const normalizedIso = String(languageIso || "").toLowerCase().trim();
    if (!languageList.some((language) => language.iso === normalizedIso)) return;
    setAiAudioLanguageIso(normalizedIso);
  }

  function continueToAiAudioDescription() {
    if (!aiAudioLanguageIso) return;
    setIsAiAudioLanguageOpen(false);
    setIsBulkAiAudioOpen(true);
  }

  const visibleLanguageIso = useMemo(() => {
    if (languageList.some((language) => language.iso === activeLanguageIso)) {
      return activeLanguageIso;
    }
    return languageList[0]?.iso || "";
  }, [activeLanguageIso, languageList]);

  const batchAudioDownload = useBatchAudioDownload({
    selectedMode,
    activeLanguageIso: visibleLanguageIso,
    modePoints,
    walkingPointsByRoute,
    tourName,
  });
  const batchAudioUpload = useBatchAudioUpload({
    selectedMode,
    activeLanguageIso: visibleLanguageIso,
    modePoints,
    walkingPointsByRoute,
    tourName,
    onUploadPointMedia,
    onCommitPoint: onCommitBulkAiAudioPoint,
  });
  const isBatchAudioBusy =
    batchAudioDownload.isDownloading || batchAudioUpload.isUploading;

  useEffect(() => {
    if (!isBatchAudioBusy) return undefined;
    const appRoot = document.getElementById("root");
    if (!appRoot) return undefined;
    const wasInert = appRoot.inert;
    appRoot.inert = true;
    return () => {
      appRoot.inert = wasInert;
    };
  }, [isBatchAudioBusy]);

  const selectedStartPointName = useMemo(
    () => resolveSelectedStartPointName(selectedStartPoint || {}, visibleLanguageIso),
    [selectedStartPoint, visibleLanguageIso],
  );

  const pointTranslation = useRoutePointTranslation({
    tourId,
    modePoints,
    onCommitPoint,
    onTranslationApplied: setActiveLanguageIso,
  });
  const pointAutomation = useRoutePointAutomation({
    tourId,
    routeType: selectedMode,
    routeRaw: directiosApiRoutePointRaw,
    tourName,
    routeName: selectedStartPointName,
    modePoints,
    onApplyPoints: onApplyAutomatedPoints,
  });
  const routeMusic = useRouteMusicGeneration({
    tourId,
    routeType: selectedMode,
    modePoints,
    languageIso: visibleLanguageIso,
    onCommitPoint,
  });
  const routeAudioMixing = useRouteAudioMixing({ tourId, onCommitPoint });

  useEffect(() => {
    if (!languageList.length) {
      setActiveLanguageIso("");
      return;
    }
    setActiveLanguageIso((currentIso) => {
      if (languageList.some((language) => language.iso === currentIso)) {
        return currentIso;
      }
      return languageList[0].iso;
    });
  }, [languageList]);

  useEffect(() => {
    onActiveLanguageChange?.(visibleLanguageIso);
  }, [onActiveLanguageChange, visibleLanguageIso]);

  if (!startPointMode) return null;

  return (
    <aside
      ref={modeToolOverlayRef}
      className="mode-route-tool-overlay"
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
    >
      <RouteActionsOverlayPanel
        anchorRef={modeToolOverlayRef}
        selectedMode={selectedMode}
        activeLanguageIso={visibleLanguageIso}
        onStopWalkingRouteShapeEdit={onStopWalkingRouteShapeEdit}
        onTranslatePoints={pointTranslation.open}
        onGenerateTurnByTurn={onGenerateTurnByTurn}
        isGeneratingTurnByTurn={isGeneratingTurnByTurn}
        turnByTurnApplied={turnByTurnApplied}
        isAiAutomationRunning={pointAutomation.isGenerating}
        onAutomatePoints={pointAutomation.openContent}
        onGenerateDirectionPoints={pointAutomation.openDirections}
        onGenerateParkingPoint={pointAutomation.openParking}
        onGenerateImages={pointAutomation.openImages}
        onGenerateAudioDescription={openAiAudioLanguagePicker}
        onGenerateMusic={routeMusic.open}
        isGeneratingMusic={routeMusic.isGenerating}
        isBatchAudioBusy={isBatchAudioBusy}
      />
      <ModeToolHeader
        onCloseTool={onCloseTool}
        selectedMode={selectedMode}
        isPickingModePoints={isPickingModePoints}
        onTogglePickingModePoints={onTogglePickingModePoints}
        onStopWalkingRouteShapeEdit={onStopWalkingRouteShapeEdit}
        selectedStartPointName={selectedStartPointName}
        languageList={languageList}
        activeLanguageIso={visibleLanguageIso}
        onChangeLanguage={setActiveLanguageIso}
        onImportJson={onImportJson}
        isImportingJson={isImportingJson}
        onExportJson={onExportJson}
        canExportJson={canExportJson}
        isBatchAudioBusy={isBatchAudioBusy}
      />
      <TranslatePointsModal
        isOpen={pointTranslation.isOpen}
        onClose={pointTranslation.close}
        languages={languageList}
        selectedLanguage={pointTranslation.selectedLanguage}
        onSelectLanguage={pointTranslation.setSelectedLanguage}
        pointScope={pointTranslation.pointScope}
        onSelectPointScope={pointTranslation.setPointScope}
        isSinglePoint={pointTranslation.isSinglePoint}
        onTranslate={pointTranslation.translate}
        isTranslating={pointTranslation.isTranslating}
        pointCount={pointTranslation.targetPointCount}
        error={pointTranslation.error}
        failedPoints={pointTranslation.failedPoints}
        streamProgress={pointTranslation.streamProgress}
      />
      <AutomateRoutePointsModal
        isOpen={pointAutomation.isOpen}
        onClose={pointAutomation.close}
        onGenerate={pointAutomation.generate}
        isGenerating={pointAutomation.isGenerating}
        error={pointAutomation.error}
        success={pointAutomation.success}
        results={pointAutomation.results}
        activeAction={pointAutomation.activeAction}
        streamProgress={pointAutomation.streamProgress}
        isSingleImagePoint={pointAutomation.isSingleImagePoint}
        imagePointScope={pointAutomation.imagePointScope}
        onImagePointScopeChange={pointAutomation.setImagePointScope}
      />
      <AiMusicModal
        isOpen={routeMusic.isOpen}
        onClose={routeMusic.close}
        onGenerate={routeMusic.generate}
        isGenerating={routeMusic.isGenerating}
        pointScope={routeMusic.pointScope}
        onSelectPointScope={routeMusic.setPointScope}
        error={routeMusic.error}
        progress={routeMusic.progress}
        pointOnly={Boolean(routeMusic.targetPointId)}
        languageIso={routeMusic.selectedLanguageIso}
        languages={languageList}
        onSelectLanguage={routeMusic.setSelectedLanguageIso}
        promptSingle={routeMusic.promptSingle}
        onPromptSingleChange={routeMusic.setPromptSingle}
      />
      <AiAudioLanguageModal
        isOpen={isAiAudioLanguageOpen}
        languages={languageList}
        selectedLanguage={aiAudioLanguageIso}
        onSelectLanguage={selectAiAudioLanguage}
        pointScope={aiAudioPointScope}
        onSelectPointScope={setAiAudioPointScope}
        onContinue={continueToAiAudioDescription}
        onClose={() => setIsAiAudioLanguageOpen(false)}
      />
      {importJsonError ? (
        <p className="message-error mode-route-tool-import-message">
          {importJsonError}
        </p>
      ) : null}
      {importJsonMessage ? (
        <p
          className={`mode-route-tool-help mode-route-tool-import-message ${
            isImportingJson ? "is-loading" : "is-success"
          }`}
        >
          {isImportingJson ? (
            <span className="mode-route-tool-btn-spinner" />
          ) : null}
          <span>{importJsonMessage}</span>
        </p>
      ) : null}
      {/*       <p className="mode-route-tool-help">Select route points and add the details you need for each route type.</p>
       */}{" "}
      <ModeToolModeList
        modeOptions={modeOptions}
        selectedMode={selectedMode}
        modePointCount={modePointCount}
        onSelectMode={onSelectMode}
        onStopWalkingRouteShapeEdit={onStopWalkingRouteShapeEdit}
        isBatchAudioBusy={isBatchAudioBusy}
      />
      <BulkAiAudioDescription
        isOpen={isBulkAiAudioOpen}
        onClose={() => setIsBulkAiAudioOpen(false)}
        targetLanguageIso={aiAudioLanguageIso}
        targetPointScope={aiAudioPointScope}
        selectedMode={selectedMode}
        modePoints={modePoints}
        selectedWalkingRouteKey={selectedWalkingRouteKey}
        walkingRouteMetaByKey={walkingRouteMetaByKey}
        walkingPointsByRoute={walkingPointsByRoute}
        onCommitPoint={onCommitPoint}
        onCommitBulkPoint={onCommitBulkAiAudioPoint}
      />
      <ModeToolPointsList
        selectedMode={selectedMode}
        modePoints={modePoints}
        directiosApiRoutePointRaw={directiosApiRoutePointRaw}
        selectedPointId={selectedPointId}
        onSelectPoint={onSelectPoint}
        languages={languages}
        activeLanguageIso={visibleLanguageIso}
        onCommitPoint={onCommitPoint}
        onTranslatePoint={pointTranslation.openForPoint}
        onGeneratePointImage={pointAutomation.openImageForPoint}
        onGeneratePointMusic={(point, pointLanguageIso) =>
          routeMusic.openForPoint(point, pointLanguageIso || visibleLanguageIso)
        }
        isGeneratingMusic={routeMusic.isGenerating}
        generatingMusicPointId={routeMusic.targetPointId}
        onMixAudioMusic={routeAudioMixing.mixPoint}
        mixingPointId={routeAudioMixing.mixingPointId}
        mixingLanguageIso={routeAudioMixing.mixingLanguageIso}
        audioMixErrorByPointLanguage={routeAudioMixing.errorByPointLanguage}
        onUploadPointMedia={onUploadPointMedia}
        onDeletePoint={onDeletePoint}
        onSaveModeRedux={onSaveModeRedux
            ? async (...args) => {
              const result = await onSaveModeRedux(...args);
              if (result === true) {
                batchAudioUpload.clearFeedback();
                batchAudioDownload.clearFeedback();
              }
              return result;
            }
          : undefined}
        canSaveModePoints={canSaveModePoints}
        modeValidationHint={modeValidationHint}
        saveModePointsHint={saveModePointsHint}
        walkingRouteKeys={walkingRouteKeys}
        selectedWalkingRouteKey={selectedWalkingRouteKey}
        walkingRouteMetaByKey={walkingRouteMetaByKey}
        isWalkingRouteShapeEditEnabled={isWalkingRouteShapeEditEnabled}
        onSelectWalkingRoute={onSelectWalkingRoute}
        onAddWalkingRoute={onAddWalkingRoute}
        onChangeWalkingRouteName={onChangeWalkingRouteName}
        onToggleWalkingRouteShapeEdit={onToggleWalkingRouteShapeEdit}
        onStopWalkingRouteShapeEdit={onStopWalkingRouteShapeEdit}
        onDeleteWalkingRoute={onDeleteWalkingRoute}
        isBatchAudioBusy={isBatchAudioBusy}
        onDownloadBatchAudios={batchAudioDownload.downloadBatchAudios}
        isDownloadingBatchAudios={batchAudioDownload.isDownloading}
        onUploadBatchAudios={batchAudioUpload.uploadBatchAudios}
        isUploadingBatchAudios={batchAudioUpload.isUploading}
        batchAudioStatus={batchAudioUpload.status || batchAudioDownload.status}
        batchAudioError={batchAudioUpload.error || batchAudioDownload.error}
      />
      <AppModal
        isOpen={isBatchAudioBusy}
        onClose={() => {}}
        closeDisabled
        title={batchAudioUpload.isUploading ? "Uploading batch audios" : "Downloading batch audios"}
        size="sm"
        className="mode-route-batch-audio-modal"
      >
        <div className="mode-route-batch-audio-progress" role="status" aria-live="polite">
          <span className="mode-route-tool-btn-spinner" aria-hidden="true" />
          <p>
            {batchAudioUpload.isUploading
              ? batchAudioUpload.status || "Uploading batch audios…"
              : batchAudioDownload.status || "Preparing batch audio ZIP…"}
          </p>
        </div>
      </AppModal>
    </aside>
  );
}

export default ModeRouteToolPanel;
